import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';

import { AuthShell } from '@/components/auth/auth-shell';
import { SelfRegistrationHero } from '@/components/auth/self-registration-hero';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  AGE_ATTESTATION_VERSION,
  PRIVACY_POLICY_VERSION,
  TERMS_OF_SERVICE_VERSION,
} from '@/constants/legal';
import {
  getPropertyRegistrationInfo,
  submitResidentSelfRegistration,
} from '@/services/public-api.client';

// KDPA §32 + §33 — privacy / terms / age-of-majority consent must be granted
// at the point of registration. Server validation enforces the same gate via
// `@Equals(true)` on each boolean in `ResidentSelfRegistrationDto`.
const schema = z.object({
  fullName: z.string().min(2, 'Full name must be at least 2 characters'),
  email: z.string().email('Enter a valid email address'),
  phone: z.string().min(9, 'Enter a valid phone number'),
  unitIdentifier: z.string().min(1, 'Unit identifier is required'),
  note: z.string().max(500, 'Note must be 500 characters or fewer').optional(),
  privacyPolicyAccepted: z.literal(true, {
    message: 'You must accept the Privacy Policy',
  }),
  termsAccepted: z.literal(true, {
    message: 'You must accept the Terms of Service',
  }),
  ageEighteenPlus: z.literal(true, {
    message: 'You must confirm you are at least 18 years old',
  }),
});

type FormValues = z.infer<typeof schema>;

export function ResidentSelfRegistrationPage() {
  const { slug } = useParams<{ slug: string }>();
  const [flagDisabled, setFlagDisabled] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const infoQuery = useQuery({
    queryKey: ['public-property-registration', slug],
    queryFn: () => getPropertyRegistrationInfo(slug ?? ''),
    enabled: !!slug,
    retry: false,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      privacyPolicyAccepted: false as unknown as true,
      termsAccepted: false as unknown as true,
      ageEighteenPlus: false as unknown as true,
    },
  });

  const privacyAccepted = watch('privacyPolicyAccepted');
  const termsAcceptedValue = watch('termsAccepted');
  const ageOk = watch('ageEighteenPlus');

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      if (!infoQuery.data) throw new Error('Property info not loaded');
      return submitResidentSelfRegistration({
        propertyId: infoQuery.data.propertyId,
        fullName: values.fullName,
        email: values.email,
        phone: values.phone,
        unitIdentifier: values.unitIdentifier,
        ...(values.note ? { note: values.note } : {}),
        privacyPolicyAccepted: true,
        privacyPolicyVersion: PRIVACY_POLICY_VERSION,
        termsAccepted: true,
        termsVersion: TERMS_OF_SERVICE_VERSION,
        ageEighteenPlus: true,
        ageAttestationVersion: AGE_ATTESTATION_VERSION,
      });
    },
    onError: (err) => {
      const status = (err as { status?: number }).status;
      if (status === 422) setFlagDisabled(true);
      else if (status === 409) setSubmitError('This email is already registered on this property.');
      else if (status === 404)
        setSubmitError('Property not found. This registration link may be invalid.');
      else setSubmitError('Registration failed. Please try again.');
    },
  });

  // ── State: loading ────────────────────────────────────────────────────────
  if (infoQuery.isLoading) {
    return (
      <AuthShell hero={<SelfRegistrationHero />} title="Loading…" description="">
        <p className="text-sm text-[var(--text-secondary)]">Looking up your property…</p>
      </AuthShell>
    );
  }

  // ── State: property not found ─────────────────────────────────────────────
  if (infoQuery.isError || !infoQuery.data) {
    return (
      <AuthShell
        hero={<SelfRegistrationHero />}
        title="Property not found"
        description="This registration link doesn't match any known property."
      >
        <p className="text-sm text-[var(--text-secondary)]">
          Check the link from your property manager, or{' '}
          <Link to="/login" className="underline underline-offset-4">
            sign in
          </Link>
          .
        </p>
      </AuthShell>
    );
  }

  const { propertyName, registrationEnabled, branding } = infoQuery.data;
  const heroProps = {
    propertyName,
    ...(branding?.logoUrl !== undefined ? { logoUrl: branding.logoUrl } : {}),
    ...(branding?.primaryColour !== undefined ? { primaryColour: branding.primaryColour } : {}),
  };

  // ── State: registration disabled on this property ─────────────────────────
  if (!registrationEnabled || flagDisabled) {
    return (
      <AuthShell
        hero={<SelfRegistrationHero {...heroProps} />}
        title={`Self-registration is not enabled for ${propertyName}`}
        description="Contact your property manager to be invited directly."
      >
        <Button asChild variant="outline">
          <Link to="/login">Back to sign in</Link>
        </Button>
      </AuthShell>
    );
  }

  // ── State: success ────────────────────────────────────────────────────────
  if (mutation.isSuccess) {
    return (
      <AuthShell
        hero={<SelfRegistrationHero {...heroProps} />}
        title="Registration submitted"
        description={`Your registration request for ${propertyName} is awaiting approval.`}
      >
        <div
          role="status"
          className="flex items-start gap-3 rounded-lg border p-4 text-sm"
          style={{
            borderColor: 'var(--status-green)',
            backgroundColor: 'var(--status-green-tint)',
            color: 'var(--text-success)',
          }}
        >
          A property manager will review your request and send you an activation email within a few
          business days.
        </div>
        <p className="mt-4 text-center text-sm text-[var(--text-secondary)]">
          <Link to="/login" className="underline underline-offset-4">
            Back to sign in
          </Link>
        </p>
      </AuthShell>
    );
  }

  // ── State: form ────────────────────────────────────────────────────────────
  const emailConflict = mutation.isError && (mutation.error as { status?: number }).status === 409;

  return (
    <AuthShell
      hero={<SelfRegistrationHero {...heroProps} />}
      title={`Request to join ${propertyName}`}
      description="Your details will be reviewed by a property manager before your account is activated."
    >
      <form
        onSubmit={(e) =>
          void handleSubmit((v) => {
            setSubmitError('');
            mutation.mutate(v);
          })(e)
        }
        noValidate
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="fullName">Full Name</FieldLabel>
            <Input
              id="fullName"
              autoComplete="name"
              disabled={mutation.isPending}
              aria-invalid={!!errors.fullName}
              {...register('fullName')}
            />
            {errors.fullName && (
              <p className="text-sm text-destructive" role="alert">
                {errors.fullName.message}
              </p>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              disabled={mutation.isPending}
              aria-invalid={!!errors.email || emailConflict}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm text-destructive" role="alert">
                {errors.email.message}
              </p>
            )}
            {emailConflict && (
              <p className="text-sm text-destructive" role="alert">
                An account with this email already exists on this property.
              </p>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="phone">Phone (E.164)</FieldLabel>
            <Input
              id="phone"
              type="tel"
              autoComplete="tel"
              placeholder="+254 700 000 000"
              disabled={mutation.isPending}
              aria-invalid={!!errors.phone}
              {...register('phone')}
            />
            {errors.phone && (
              <p className="text-sm text-destructive" role="alert">
                {errors.phone.message}
              </p>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="unitIdentifier">House / Unit Number</FieldLabel>
            <Input
              id="unitIdentifier"
              placeholder="e.g. House 12 or Apt 4B"
              disabled={mutation.isPending}
              aria-invalid={!!errors.unitIdentifier}
              {...register('unitIdentifier')}
            />
            <p className="text-xs text-muted-foreground">
              Enter your house or unit number as you know it.
            </p>
            {errors.unitIdentifier && (
              <p className="text-sm text-destructive" role="alert">
                {errors.unitIdentifier.message}
              </p>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="note">Note for the property manager (optional)</FieldLabel>
            <Input
              id="note"
              placeholder="e.g. Move-in date or unit owner reference"
              disabled={mutation.isPending}
              aria-invalid={!!errors.note}
              {...register('note')}
            />
            {errors.note && (
              <p className="text-sm text-destructive" role="alert">
                {errors.note.message}
              </p>
            )}
          </Field>

          {/* KDPA §32 + §33 — consent + age-of-majority gate */}
          <Field>
            <FieldLabel className="text-sm font-medium">Privacy &amp; consent</FieldLabel>
            <div className="space-y-3 rounded-md border border-[var(--border-default)] bg-[var(--bg-surface)] p-3">
              <label className="flex items-start gap-3 text-sm">
                <Checkbox
                  id="ageEighteenPlus"
                  checked={!!ageOk}
                  onCheckedChange={(v) =>
                    setValue(
                      'ageEighteenPlus',
                      v === true ? (true as true) : (false as unknown as true),
                      { shouldValidate: true },
                    )
                  }
                  aria-invalid={!!errors.ageEighteenPlus}
                />
                <span>
                  I confirm that I am at least <strong>18 years old</strong>.
                </span>
              </label>
              {errors.ageEighteenPlus && (
                <p className="text-xs text-destructive" role="alert">
                  {errors.ageEighteenPlus.message as string}
                </p>
              )}

              <label className="flex items-start gap-3 text-sm">
                <Checkbox
                  id="privacyPolicyAccepted"
                  checked={!!privacyAccepted}
                  onCheckedChange={(v) =>
                    setValue(
                      'privacyPolicyAccepted',
                      v === true ? (true as true) : (false as unknown as true),
                      { shouldValidate: true },
                    )
                  }
                  aria-invalid={!!errors.privacyPolicyAccepted}
                />
                <span>
                  I have read and accept the{' '}
                  <Link to="/privacy" target="_blank" className="underline underline-offset-4">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
              {errors.privacyPolicyAccepted && (
                <p className="text-xs text-destructive" role="alert">
                  {errors.privacyPolicyAccepted.message as string}
                </p>
              )}

              <label className="flex items-start gap-3 text-sm">
                <Checkbox
                  id="termsAccepted"
                  checked={!!termsAcceptedValue}
                  onCheckedChange={(v) =>
                    setValue(
                      'termsAccepted',
                      v === true ? (true as true) : (false as unknown as true),
                      { shouldValidate: true },
                    )
                  }
                  aria-invalid={!!errors.termsAccepted}
                />
                <span>
                  I have read and accept the{' '}
                  <Link to="/terms" target="_blank" className="underline underline-offset-4">
                    Terms of Service
                  </Link>
                  .
                </span>
              </label>
              {errors.termsAccepted && (
                <p className="text-xs text-destructive" role="alert">
                  {errors.termsAccepted.message as string}
                </p>
              )}
            </div>
          </Field>

          <Field>
            {submitError && <p className="text-sm text-[var(--status-red-text)] mb-2">{submitError}</p>}
            <Button
              type="submit"
              disabled={mutation.isPending || !ageOk || !privacyAccepted || !termsAcceptedValue}
              style={
                branding?.primaryColour ? { backgroundColor: branding.primaryColour } : undefined
              }
            >
              {mutation.isPending ? 'Submitting…' : 'Submit Registration'}
            </Button>
          </Field>

          <p className="text-center text-sm text-[var(--text-secondary)]">
            <Link to="/login" className="underline underline-offset-4">
              Already have an account? Sign in
            </Link>
          </p>
        </FieldGroup>
      </form>
    </AuthShell>
  );
}
